/**
 * Small building blocks shared by the Comptoir screens: number fields with a
 * unit, section cards, empty states, client avatars and the formatting helpers.
 */
import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { moduleColors } from '../../theme/tableTheme';

export const COLOR = moduleColors.comptoir.primary;
export const HOVER = moduleColors.comptoir.hover;

/* ---------------- formatting ---------------- */
export const num = (v: string | number | null | undefined): number => {
  const n = parseFloat(String(v ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
export const r2 = (v: number) => Math.round(v * 100) / 100;
export const dh = (v: number | string | null | undefined) =>
  `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DH`;
export const fDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
export const today = () => new Date().toISOString().split('T')[0];
export const digits = (s?: string | null) => (s || '').replace(/\D/g, '');

/** Same rule as the backend (comptoir_service.phone_valid): 9 to 15 digits. */
export const phoneValid = (tel: string) => {
  const d = digits(tel);
  return d.length >= 9 && d.length <= 15;
};
/** "0612345678" → "06 12 34 56 78" (display only; what the user typed is stored). */
export const fmtPhone = (tel?: string | null) => {
  const d = digits(tel);
  return d.length === 10 && d.startsWith('0') ? d.replace(/(\d{2})(?=\d)/g, '$1 ').trim() : tel || '';
};

export const initials = (name?: string | null) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

/* ---------------- components ---------------- */

/** Decimal input: accepts "12,5", shows a unit, numbers right-aligned in a mono font. */
export const NumberField: React.FC<
  Omit<TextFieldProps, 'onChange' | 'value'> & { value: string; onChange: (v: string) => void; unit?: string }
> = ({ value, onChange, unit, InputProps, inputProps, ...rest }) => (
  <TextField
    {...rest}
    value={value}
    onChange={(e) => {
      const v = e.target.value.replace(/\s/g, '');
      if (/^\d*[.,]?\d{0,2}$/.test(v)) onChange(v);
    }}
    inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', fontFamily: 'monospace' }, ...inputProps }}
    InputProps={{
      ...(unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : {}),
      ...InputProps,
    }}
  />
);

export const ClientAvatar: React.FC<{ name?: string | null; size?: number }> = ({ name, size = 36 }) => (
  <Avatar sx={{ width: size, height: size, fontSize: size * 0.4, fontWeight: 600, bgcolor: alpha(COLOR, 0.15), color: COLOR }}>
    {initials(name)}
  </Avatar>
);

export const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  error?: boolean;
  children: React.ReactNode;
  id?: string;
}> = ({ icon, title, subtitle, action, error, children, id }) => (
  <Paper
    id={id}
    variant="outlined"
    sx={{ p: 2.5, mb: 2.5, borderRadius: 2, borderColor: error ? 'error.main' : 'divider', transition: 'border-color .2s' }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(COLOR, 0.1), color: COLOR,
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {action}
    </Stack>
    {children}
  </Paper>
);

export const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  text?: string;
  action?: { label: string; onClick: () => void; icon?: React.ReactNode };
  compact?: boolean;
}> = ({ icon, title, text, action, compact }) => (
  <Box sx={{
    textAlign: 'center', py: compact ? 3 : 6, px: 2, borderRadius: 2,
    border: '1px dashed', borderColor: 'divider', bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
  }}>
    <Box sx={{ color: alpha(COLOR, 0.6), '& svg': { fontSize: compact ? 36 : 52 } }}>{icon}</Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>{title}</Typography>
    {text && <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mt: 0.5 }}>{text}</Typography>}
    {action && (
      <Button variant="outlined" startIcon={action.icon} onClick={action.onClick} sx={{ mt: 2, borderColor: COLOR, color: COLOR }}>
        {action.label}
      </Button>
    )}
  </Box>
);

/** One "label … value" line of a totals panel. */
export const TotalLine: React.FC<{ label: string; value: string; strong?: boolean; color?: string }> = ({ label, value, strong, color }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 0.75 }}>
    <Typography variant={strong ? 'subtitle1' : 'body2'} sx={{ fontWeight: strong ? 700 : 400 }} color={strong ? 'text.primary' : 'text.secondary'}>
      {label}
    </Typography>
    <Typography variant={strong ? 'h6' : 'body2'} sx={{ fontFamily: 'monospace', fontWeight: strong ? 700 : 500, color }}>
      {value}
    </Typography>
  </Stack>
);

export const primaryBtnSx = { bgcolor: COLOR, '&:hover': { bgcolor: HOVER } };

/* ---------------- Services & Projets: derived états ---------------- */
type ChipTone = { bg: string; fg: string; outlined?: boolean };
const tone = (color: string, outlined = false): ChipTone => ({ bg: outlined ? 'transparent' : color, fg: outlined ? color : '#fff', outlined });

export const ETAT_TONE: Record<string, ChipTone> = {
  avance: tone('#ED6C02'),
  en_cours: tone(COLOR),
  livre_partiel: tone('#7B1FA2'),
  livre: tone('#2E7D32'),
  cloture: tone('#607D8B'),
  // prototype V8 states (etat_com)
  pret_lancer: tone('#1565C0'),
  prod: tone('#3949AB'),
  pret: tone('#7B1FA2'),
};
export const PAIEMENT_TONE: Record<string, ChipTone> = {
  na: tone('#607D8B', true),
  attente: tone('#ED6C02', true),
  avance: tone(COLOR, true),
  partiel: tone(COLOR, true),
  du: tone('#D32F2F', true),
  solde: tone('#2E7D32', true),
};

export const StateChip: React.FC<{ label: string; t?: ChipTone; small?: boolean }> = ({ label, t, small }) => (
  <Box component="span" sx={{
    display: 'inline-flex', alignItems: 'center', px: 1.25, height: small ? 22 : 26, borderRadius: 13,
    fontSize: small ? 11.5 : 12.5, fontWeight: 600, whiteSpace: 'nowrap',
    bgcolor: t?.bg || 'action.selected', color: t?.fg || 'text.primary',
    border: t?.outlined ? `1.5px solid ${t.fg}` : 'none',
  }}>
    {label}
  </Box>
);

/* ---------------- detail pages (service, client) ---------------- */
export const PANEL_SX = { borderRadius: 2, overflow: 'hidden', mb: 2.5 };

export const Panel: React.FC<{ title: string; icon: React.ReactNode; count?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, count, action, children }) => (
  <Paper variant="outlined" sx={PANEL_SX}>
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(COLOR, 0.04) }}>
      <Box sx={{ color: COLOR, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>{title}</Typography>
      {count != null && <Typography variant="body2" color="text.secondary">{count}</Typography>}
      {action}
    </Stack>
    {children}
  </Paper>
);

export const Fact: React.FC<{ label: string; children: React.ReactNode; danger?: boolean }> = ({ label, children, danger }) => (
  <Grid item xs={6} md={3}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: danger ? 'error.main' : undefined }} component="div">{children}</Typography>
  </Grid>
);
